import { List, OrderedMap, OrderedSet, Set } from 'immutable';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import { PageId } from 'records/Page';
import { Project } from 'records/Project';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { RFI, StudioData } from 'records/RFI';
import { RFIAttachment } from 'records/RFIAttachment';
import { RFIFile } from 'records/RFIFile';
import { SheetVersion } from 'records/SheetVersion';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { User } from 'records/User';
import { StateMachine } from 'xstate';

export type ViewRFIMachineServices = {
  sendOfficialResponse: (context: ViewRFIContext) => Promise<RFI>;
  sendForwardMessage: (context: ViewRFIContext) => Promise<RFI>;
  sendForwardReply: (context: ViewRFIContext) => Promise<RFI>;
  attachDiscussionToResponse: (context: ViewRFIContext) => Promise<RFI>;
  getSheetVersionsFromPageIds: (pageids: Set<string>) => List<SheetVersion>;
  getUserRole: (
    requiredContext: ViewRFIMachineRequiredContext
  ) => RFIResponseRoleEnum;
  getUserByEmail: (email: string) => User;
  updateAttachments: (
    event: UpdateAttachmentEvent,
    rfiAttachments: RFIAttachments,
    projectId?: string
  ) => RFIAttachments;
  removeAttachments: (
    event: RemoveAttachmentEvent,
    rfiAttachments: RFIAttachments
  ) => RFIAttachments;
  deleteAttachedDiscussion: (context: ViewRFIContext) => Promise<RFI>;
  reopenRfi: (context: ViewRFIContext) => Promise<RFI>;
  getRfiVersionList: (rfiid: string) => OrderedMap<string, RFI>;
  saveDraft: (context: ViewRFIContext) => Promise<void>;
  updateDraft: (context: ViewRFIContext) => Promise<RFI>;
  publishDraft: (context: ViewRFIContext) => Promise<RFI>;
  deleteDraft: (context: ViewRFIContext) => Promise<void>;
};

export enum AttachmentTypeEnum {
  STUDIO = 'STUDIO',
  DRAWING = 'DRAWING',
  DRIVE = 'DRIVE',
  INTEGRATION = 'INTEGRATION',
  FILE = 'FILE',
  SAVED = 'SAVED',
}

export type RFIAttachments = {
  [AttachmentTypeEnum.SAVED]: List<RFIAttachment>;
  [AttachmentTypeEnum.FILE]: List<RFIFile>;
  [AttachmentTypeEnum.STUDIO]: List<StudioProjectFileData>;
  [AttachmentTypeEnum.DRIVE]: List<ProjectDriveSelectedItem>;
  [AttachmentTypeEnum.INTEGRATION]: List<IntegrationsSelectedItem>;
  [AttachmentTypeEnum.DRAWING]: OrderedSet<string>;
  drawingSheetVersions: List<SheetVersion>;
  studioData: StudioData;
};

export type RFIResponse = {
  message: string;
  attachments: RFIAttachments;
};

export type ForwardMessage = RFIResponse & {
  forwardList: OrderedSet<string>;
};

type RFIRevision = {
  rfi: RFI;
  attachments: RFIAttachments;
  attachmentsToRemove: List<RFIAttachment>;
};

export type AppPages = 'PUNCHES' | 'VIEWER' | 'MAP';

export type ViewRFIMachineRequiredContext = {
  rfi: RFI;
  currentUser: User;
  userEmails: OrderedSet<string>;
  project: Project;
  appPage: AppPages;
};

export type ViewRFIContext = ViewRFIMachineRequiredContext & {
  role: RFIResponseRoleEnum;
  officialResponse: RFIResponse;
  rfiAttachmentSheetVersions: List<SheetVersion>;
  forwardList: OrderedSet<string>;
  forwardMessage: ForwardMessage;
  forwardReply: RFIResponse;
  selectedDiscussionIds: OrderedSet<string>;
  rfiRevision: RFIRevision;
  rfiVersionList: OrderedMap<string, RFI>;
};

export type ViewRFIMachineProps = {
  services: ViewRFIMachineServices;
  requiredContext: ViewRFIMachineRequiredContext;
};

export enum RFIResponseRoleEnum {
  consultant = 'consultant',
  assignee = 'assignee',
  admin = 'admin',
  unauthorized = 'unauthorized',
}

export enum ViewRFIStatesEnum {
  responding = 'responding',
  pendingOfficialResponse = 'pendingOfficialResponse',
  creatingOfficialResponse = 'creatingOfficialResponse',
  sendingOfficialResponse = 'sendingOfficialResponse',
  discussing = 'discussing',
  pendingForward = 'pendingForward',
  creatingForwardMessage = 'creatingForwardMessage',
  sendingForwardMessage = 'sendingForwardMessage',
  pendingForwardReply = 'pendingForwardReply',
  creatingForwardReply = 'creatingForwardReply',
  closed = 'closed',
  done = 'done',
  pendingDiscussionAttachment = 'pendingDiscussionAttachment',
  sendingForwardReply = 'sendingForwardReply',
  attachingDiscussionToResponse = 'attachingDiscussionToResponse',
  discussionAttached = 'discussionAttached',
  viewingAttachedDiscussions = 'viewingAttachedDiscussions',
  deletingAttachedDiscussion = 'deletingAttachedDiscussion',
  cancellingWithDiscussionAttachment = 'cancellingWithDiscussionAttachment',
  reopeningRfi = 'reopeningRfi',
  creatingRfiVersion = 'creatingRfiVersion',
  savingRfiDraft = 'savingRfiDraft',
  creatingRfiDraft = 'creatingRfiDraft',
  publishingRfiDraft = 'publishingRfiDraft',
  updatingRfiDraft = 'updatingRfiDraft',
  deletingRfiDraft = 'deletingRfiDraft',
  deletingRfiDraftConfirm = 'deletingRfiDraftConfirm',
  reopened = 'reopened',
  reopenedRfiOnViewerOrMapPage = 'reopenedRfiOnViewerOrMapPage',
}

export type ViewRFIStates = {
  states: {
    [key in ViewRFIStatesEnum]: Record<string, unknown>;
  };
};

export enum ViewRFIEventsEnum {
  RFI_SELECTED = 'RFI_SELECTED',
  CLOSE = 'CLOSE',
  CANCEL = 'CANCEL',
  RESPOND = 'RESPOND',
  SEND = 'SEND',
  DISCUSS = 'DISCUSS',
  FORWARD = 'FORWARD',
  ATTACH_DISCUSSION = 'ATTACH_DISCUSSION',
  REPLY = 'REPLY',
  UPDATE = 'UPDATE',
  UPDATE_ATTACHMENT = 'UPDATE_ATTACHMENT',
  UPDATE_FORWARD_LIST = 'UPDATE_FORWARD_LIST',
  UPDATE_SELECTED_DISCUSSIONS = 'SELECT_DISCUSSION',
  VIEW_ATTACHED_DISCUSSION = 'VIEW_ATTACHED_DISCUSSION',
  DELETE_DISCUSSION_ATTACHMENT = 'DELETE_DISCUSSION_ATTACHMENT',
  CANCEL_WITH_DISCUSSION_ATTACHMENT = 'CANCEL_WITH_DISCUSSION_ATTACHMENT',
  REOPEN_RFI = 'REOPEN_RFI',
  CREATE_RFI = 'CREATE_RFI',
  UPDATE_RFI = 'UPDATE_RFI',
  REMOVE_ATTACHMENT = 'REMOVE_ATTACHMENT',
  SAVE = 'SAVE',
  DELETE = 'DELETE',
  DELETE_CONFIRM = 'DELETE_CONFIRM',
}

export type RemoveAttachmentTypes =
  | RFIAttachment
  | File
  | PageId
  | ProjectDriveSelectedItem
  | IntegrationsSelectedItem
  | StudioProjectFileData;

export type UpdateAttachmentTypes =
  | OrderedSet<string>
  | List<RFIAttachment>
  | List<ProjectDriveSelectedItem>
  | List<IntegrationsSelectedItem>
  | List<StudioProjectFileData>
  | List<RFIFile>
  | List<SheetVersion>
  | FileList;

export type CancelEvent = { type: ViewRFIEventsEnum.CANCEL; data: undefined };

type RemoveAttachmentEvent = {
  type: ViewRFIEventsEnum.REMOVE_ATTACHMENT;
  data: {
    item: RemoveAttachmentTypes;
    meta?: StudioData;
    source: AttachmentTypeEnum;
  };
};

type UpdateAttachmentEvent = {
  type: ViewRFIEventsEnum.UPDATE_ATTACHMENT;
  data: {
    item: UpdateAttachmentTypes;
    meta?: StudioData;
    source: AttachmentTypeEnum;
  };
};

export type ViewRFIEvents =
  | CancelEvent
  | RemoveAttachmentEvent
  | UpdateAttachmentEvent
  | { type: ViewRFIEventsEnum.FORWARD; data: undefined }
  | { type: ViewRFIEventsEnum.SEND; data: undefined }
  | { type: ViewRFIEventsEnum.ATTACH_DISCUSSION; data: undefined }
  | { type: ViewRFIEventsEnum.REPLY; data: undefined }
  | { type: ViewRFIEventsEnum.RESPOND; data: undefined }
  | { type: ViewRFIEventsEnum.CLOSE; data: undefined }
  | { type: ViewRFIEventsEnum.DISCUSS; data: undefined }
  | { type: ViewRFIEventsEnum.VIEW_ATTACHED_DISCUSSION; data: undefined }
  | {
      type: ViewRFIEventsEnum.CANCEL_WITH_DISCUSSION_ATTACHMENT;
      data: { id: string };
    }
  | { type: ViewRFIEventsEnum.SAVE; data: undefined }
  | { type: ViewRFIEventsEnum.REOPEN_RFI; data: undefined }
  | { type: ViewRFIEventsEnum.CREATE_RFI; data: undefined }
  | { type: ViewRFIEventsEnum.DELETE; data: undefined }
  | { type: ViewRFIEventsEnum.DELETE_CONFIRM; data: undefined }
  | {
      type: ViewRFIEventsEnum.DELETE_DISCUSSION_ATTACHMENT;
      data: { id: string };
    }
  | {
      type: ViewRFIEventsEnum.UPDATE_SELECTED_DISCUSSIONS;
      data: { ids: OrderedSet<string> };
    }
  | {
      type: ViewRFIEventsEnum.UPDATE;
      data: { value: string };
    }
  | {
      type: ViewRFIEventsEnum.UPDATE_FORWARD_LIST;
      data: { forwardList: OrderedSet<string> };
    }
  | {
      type: ViewRFIEventsEnum.RFI_SELECTED;
      data: RFI;
    }
  | {
      type: ViewRFIEventsEnum.UPDATE_RFI;
      data: RFI;
    };

export type ViewRFIStateMachine = StateMachine<
  ViewRFIContext,
  ViewRFIStates,
  ViewRFIEvents
>;
export interface RFIFileAttachments {
  DRIVE: List<ProjectDriveSelectedItem>;
  INTEGRATION: List<IntegrationsSelectedItem>;
  FILE: List<RFIFile>;
  STUDIO: List<StudioProjectFileData>;
  DRAWING: OrderedSet<string>;
  SAVED: List<RFIAttachment>;
  drawingSheetVersions: List<SheetVersion>;
  studioData: StudioData;
}

export const EmptyStudioData = (): StudioData => ({
  studioSelectedFolders: [],
  selectedProject: '',
  studioCurrentProjectId: '',
  studioFolderParentId: '',
});

export const EmptyAttachments = (): RFIFileAttachments => ({
  DRIVE: List<ProjectDriveSelectedItem>(),
  INTEGRATION: List<IntegrationsSelectedItem>(),
  FILE: List<RFIFile>(),
  STUDIO: List<StudioProjectFileData>(),
  DRAWING: OrderedSet<string>(),
  SAVED: List<RFIAttachment>(),
  drawingSheetVersions: List<SheetVersion>(),
  studioData: EmptyStudioData(),
});

export const EmptyRFIResponse = (): RFIResponse => ({
  message: '',
  attachments: EmptyAttachments(),
});

export const EmptyForwardMessage = (): ForwardMessage => ({
  ...EmptyRFIResponse(),
  forwardList: OrderedSet<string>(),
});

export const EmptyRFIRevision = (): RFIRevision => ({
  rfi: new RFI(),
  attachments: EmptyAttachments(),
  attachmentsToRemove: List<RFIAttachment>(),
});
